import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useLocation, Link, useNavigate } from 'react-router-dom'
import { Palette, Moon, Sun, ChevronRight, Search, X, Users, TrendingUp, Contact, CheckSquare, Building2 } from 'lucide-react'
import { useUIStore, APP_BACKGROUNDS } from '@/store/uiStore'
import { useBreadcrumbStore } from '@/store/breadcrumbStore'
import { NotificationBell } from '@/components/ui/NotificationBell'
import { cn } from '@/utils/cn'
import type { ThemeAccent } from '@/types'
import apiClient from '@/api/client'

interface BreadcrumbItem {
  label: string
  path?: string
}

// Map routes to breadcrumbs
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function getBreadcrumbs(pathname: string, detailTitle?: string | null): BreadcrumbItem[] {
  // Home page itself — no parent crumb needed
  if (pathname === '/' || pathname === '/home') {
    return [{ label: 'Home' }]
  }

  const crumbs: BreadcrumbItem[] = [{ label: 'Home', path: '/home' }]

  const segments = pathname.split('/').filter(Boolean)
  let currentPath = ''

  const LABELS: Record<string, string> = {
    home: 'Home',
    dashboard: 'Dashboard',
    leads: 'Leads',
    deals: 'Deals',
    pipeline: 'Pipeline',
    contacts: 'Contacts',
    organizations: 'Organizations',
    'crm-organizations': 'Organizations',
    workspaces: 'Workspaces',
    activities: 'Activities',
    tasks: 'Tasks',
    calls: 'Calls',
    meetings: 'Meetings',
    emails: 'Emails',
    reports: 'Reports',
    calendar: 'Calendar',
    workflows: 'Workflows',
    notifications: 'Notifications',
    'email-templates': 'Email Templates',
    'audit-logs': 'Audit Logs',
    admin: 'Admin',
    users: 'Users',
    roles: 'Roles & Permissions',
    teams: 'Teams',
    settings: 'Settings',
    profile: 'Profile',
  }

  segments.forEach((seg, idx) => {
    currentPath += `/${seg}`
    const isLast = idx === segments.length - 1
    // A UUID segment is an entity id — show the entity's name (published by the
    // detail page) instead of the raw UUID; fall back to "Details".
    let label: string
    if (UUID_RE.test(seg)) {
      label = isLast && detailTitle ? detailTitle : 'Details'
    } else {
      label = LABELS[seg] || seg.charAt(0).toUpperCase() + seg.slice(1)
    }
    crumbs.push({ label, path: isLast ? undefined : currentPath })
  })

  return crumbs
}

const ACCENT_OPTIONS: Array<{ key: ThemeAccent; hex: string; label: string }> = [
  { key: 'blue', hex: '#2E6BE6', label: 'Blue' },
  { key: 'purple', hex: '#7C3AED', label: 'Purple' },
  { key: 'green', hex: '#059669', label: 'Green' },
  { key: 'rose', hex: '#E11D48', label: 'Rose' },
  { key: 'indigo', hex: '#4F46E5', label: 'Indigo' },
]

const TYPE_ICONS: Record<string, React.ElementType> = {
  lead: Users, deal: TrendingUp, contact: Contact, task: CheckSquare, organization: Building2,
}
const TYPE_COLORS: Record<string, string> = {
  lead: '#3B82F6', deal: '#8B5CF6', contact: '#10B981', task: '#F59E0B', organization: '#EC4899',
}

function GlobalSearch() {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()

  const search = useCallback(async (q: string) => {
    if (q.trim().length < 2) { setResults(null); return }
    setLoading(true)
    try {
      const res = await apiClient.get(`/search?q=${encodeURIComponent(q)}&limit=4`)
      setResults(res.data?.data || res.data)
    } catch { setResults(null) }
    setLoading(false)
  }, [])

  useEffect(() => {
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => search(query), 300)
    return () => clearTimeout(debounceRef.current)
  }, [query, search])

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  function handleKey(e: React.KeyboardEvent) {
    if (e.key === 'Escape') { setOpen(false); setQuery('') }
  }

  const allResults = results
    ? [
        ...(results.results?.leads || []),
        ...(results.results?.deals || []),
        ...(results.results?.contacts || []),
        ...(results.results?.tasks || []),
        ...(results.results?.organizations || []),
      ]
    : []

  return (
    <div ref={containerRef} className="relative w-full max-w-sm">
      <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-slate-700 border border-mist dark:border-slate-600 rounded-lg px-2.5 py-1.5 w-full">
        <Search size={13} className="text-steel dark:text-slate-400 flex-shrink-0" />
        <input
          ref={inputRef}
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true) }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKey}
          placeholder="Search leads, deals, contacts…"
          aria-label="Search leads, deals and contacts"
          className="flex-1 text-xs bg-transparent outline-none text-navy dark:text-slate-100 placeholder-steel/60 dark:placeholder-slate-500 min-w-0"
        />
        {query && (
          <button onClick={() => { setQuery(''); setResults(null) }} className="flex-shrink-0">
            <X size={11} className="text-steel hover:text-navy dark:hover:text-slate-200" />
          </button>
        )}
      </div>

      {open && (query.length >= 2) && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-mist dark:border-slate-700 rounded-xl shadow-xl z-50 overflow-hidden">
          {loading && (
            <div className="flex items-center justify-center py-4">
              <div className="w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          {!loading && allResults.length === 0 && (
            <p className="text-xs text-steel dark:text-slate-400 text-center py-4">No results for "{query}"</p>
          )}
          {!loading && allResults.length > 0 && (
            <div className="py-1 max-h-72 overflow-y-auto">
              {allResults.map((item: any) => {
                const Icon = TYPE_ICONS[item.type] || Users
                const color = TYPE_COLORS[item.type] || '#3B82F6'
                return (
                  <button
                    key={`${item.type}-${item.id}`}
                    onClick={() => { navigate(item.url); setOpen(false); setQuery('') }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-cloud dark:hover:bg-slate-700 transition-colors text-left"
                  >
                    <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}20` }}>
                      <Icon size={12} style={{ color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-navy dark:text-slate-100 truncate">{item.title}</p>
                      <p className="text-[10px] text-steel dark:text-slate-400 truncate">{item.subtitle}</p>
                    </div>
                    <span className="text-[9px] font-semibold capitalize px-1.5 py-0.5 rounded-full flex-shrink-0" style={{ backgroundColor: `${color}15`, color }}>
                      {item.type}
                    </span>
                  </button>
                )
              })}
              <div className="border-t border-mist dark:border-slate-700 px-3 py-1.5">
                <p className="text-[10px] text-steel dark:text-slate-500">{results?.total || 0} results total</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export function Topbar() {
  const location = useLocation()
  const { isDarkMode, accentColor, appBackground, toggleDarkMode, setAccent, setAppBackground } = useUIStore()

  const [themePickerOpen, setThemePickerOpen] = useState(false)

  const themeRef = useRef<HTMLDivElement>(null)

  const detailTitle = useBreadcrumbStore((s) => s.title)
  const breadcrumbs = getBreadcrumbs(location.pathname, detailTitle)

  // Close popover on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (themeRef.current && !themeRef.current.contains(e.target as Node)) {
        setThemePickerOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  return (
    <header className="h-11 border-b flex items-center px-4 gap-3 flex-shrink-0 theme-topbar relative z-20">
      {/* Mobile brand (replaces the old hamburger — navigation lives in the
          bottom nav + More drawer on mobile) */}
      <div className="lg:hidden flex items-center gap-2 min-w-0">
        <img src="/artech-icon-v2.png" alt="" className="w-6 h-6 rounded" />
        <span className="text-sm font-bold text-navy dark:text-slate-100 truncate">AR SalesMine</span>
      </div>

      {/* Breadcrumb — left side */}
      <nav className="hidden lg:flex items-center gap-1 flex-shrink-0 min-w-0" aria-label="Breadcrumb">
        {breadcrumbs.map((crumb, idx) => (
          <React.Fragment key={idx}>
            {idx > 0 && (
              <ChevronRight
                size={12}
                className="text-gray-300 dark:text-slate-600 flex-shrink-0"
              />
            )}
            {crumb.path && idx < breadcrumbs.length - 1 ? (
              <Link
                to={crumb.path}
                className="text-xs text-steel dark:text-slate-400 hover:text-accent transition-colors truncate max-w-[100px]"
                style={{ '--hover-color': 'var(--accent)' } as React.CSSProperties}
              >
                {crumb.label}
              </Link>
            ) : (
              <span
                className={cn(
                  'text-xs truncate max-w-[150px]',
                  idx === breadcrumbs.length - 1
                    ? 'font-medium text-navy dark:text-slate-100'
                    : 'text-steel dark:text-slate-400'
                )}
              >
                {crumb.label}
              </span>
            )}
          </React.Fragment>
        ))}
      </nav>

      {/* Global Search — centred */}
      <div className="flex-1 flex justify-center">
        <GlobalSearch />
      </div>

      {/* Right actions */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {/* Notification bell (replaced placeholder with real component) */}
        <NotificationBell />

        {/* Theme picker */}
        <div ref={themeRef} className="relative">
          <button
            onClick={() => {
              setThemePickerOpen((o) => !o)
            }}
            className="w-8 h-8 flex items-center justify-center rounded-md text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
            aria-label="Theme settings"
          >
            <Palette size={16} />
          </button>

          {/* Theme picker popover */}
          {themePickerOpen && (
            <div className="absolute right-0 top-full mt-2 w-52 rounded-xl z-50 p-3 animate-fade-in" style={isDarkMode ? { background: 'rgba(15,23,42,0.98)', border: '1px solid rgba(255,255,255,0.12)', boxShadow: '0 16px 48px rgba(0,0,0,0.45), 0 4px 12px rgba(0,0,0,0.25)' } : { background: '#FFFFFF', border: '1.5px solid rgba(13,31,78,0.15)', boxShadow: '0 16px 48px rgba(13,31,78,0.18), 0 4px 12px rgba(0,0,0,0.12)' }}>
              {/* Dark mode toggle */}
              <div className="flex items-center justify-between mb-3 pb-3" style={{ borderBottom: isDarkMode ? '1px solid rgba(255,255,255,0.10)' : '1px solid rgba(13,31,78,0.10)' }}>
                <div className="flex items-center gap-2">
                  {isDarkMode ? (
                    <Moon size={14} style={{ color: isDarkMode ? '#e2e8f0' : '#0D1F4E' }} />
                  ) : (
                    <Sun size={14} style={{ color: '#0D1F4E' }} />
                  )}
                  <span className="text-xs font-medium" style={{ color: isDarkMode ? '#e2e8f0' : '#0D1F4E' }}>
                    {isDarkMode ? 'Dark Mode' : 'Light Mode'}
                  </span>
                </div>
                <button
                  onClick={toggleDarkMode}
                  className={cn(
                    'relative w-9 h-5 rounded-full transition-colors duration-200 focus:outline-none',
                    isDarkMode ? 'bg-accent' : 'bg-gray-200 dark:bg-slate-600'
                  )}
                  style={isDarkMode ? { backgroundColor: 'var(--accent)' } : undefined}
                  role="switch"
                  aria-checked={isDarkMode}
                  aria-label="Toggle dark mode"
                >
                  <span
                    className={cn(
                      'absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200',
                      isDarkMode ? 'translate-x-4' : 'translate-x-0'
                    )}
                  />
                </button>
              </div>

              {/* Accent color swatches */}
              <p className="text-[10px] font-semibold uppercase tracking-widest mb-2" style={{ color: isDarkMode ? 'rgba(148,163,184,1)' : 'rgba(13,31,78,0.55)' }}>
                Accent Color
              </p>
              <div className="grid grid-cols-5 gap-1.5">
                {ACCENT_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setAccent(opt.key)}
                    className={cn(
                      'w-full aspect-square rounded-full transition-transform hover:scale-110 focus:outline-none',
                      accentColor === opt.key && 'ring-2 ring-offset-2 ring-current'
                    )}
                    style={{
                      backgroundColor: opt.hex,
                      color: opt.hex,
                    }}
                    title={opt.label}
                    aria-label={`Set ${opt.label} accent`}
                    aria-pressed={accentColor === opt.key}
                  />
                ))}
              </div>

              {/* Background picker */}
              <p className="text-[10px] font-semibold uppercase tracking-widest mt-4 mb-2" style={{ color: isDarkMode ? 'rgba(148,163,184,1)' : 'rgba(13,31,78,0.55)' }}>
                Background
              </p>
              <div className="grid grid-cols-4 gap-2">
                {APP_BACKGROUNDS.map((bg) => (
                  <button
                    key={bg.key}
                    onClick={() => setAppBackground(bg.key)}
                    className={cn(
                      'h-9 rounded-lg border transition-transform hover:scale-[1.04] focus:outline-none',
                      appBackground === bg.key
                        ? 'ring-2 ring-offset-1 ring-[var(--accent)] border-transparent'
                        : 'border-black/10 dark:border-white/10'
                    )}
                    style={{ background: bg.swatch }}
                    title={bg.label}
                    aria-label={`Set ${bg.label} background`}
                    aria-pressed={appBackground === bg.key}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}

export default Topbar
